import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import {
  contactEventData,
  revealEventData,
  RevealOutcome,
} from "components/contacts/cognism/contactTracking";
import { ContactChannelList } from "components/contacts/contact_channels/ContactChannelList";
import {
  ContactChannelData,
  visibleChannels,
} from "components/contacts/contact_channels/contactChannels";
import Button from "components/ui/button/Button";
import { toast } from "components/ui/toast/Toast";
import Tooltip from "components/ui/tooltip/Tooltip";
import { RevealContactMutation } from "lib/generated/app-service-gql/graphql";
import { useRevealContact, useRevealContacts } from "lib/hooks/api/contacts/useRevealContacts";
import { useSeniorityTierOptions } from "lib/hooks/api/contacts/useSeniorityTierOptions";
import { useDismissLeadSuggestedContact } from "lib/hooks/api/leads/useDismissLeadSuggestedContact";
import { useGenerateMoreLeadSuggestedContacts } from "lib/hooks/api/leads/useGenerateMoreLeadSuggestedContacts";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import { EntityActionParams, useLeadEntityActions } from "lib/hooks/api/leads/useLeadEntityActions";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { CognismContactSource, RevealStatus } from "lib/types/graphQLEnums";
import { ContactHint, LeadContactCard } from "./LeadContactCard";

type SuggestedContact = NonNullable<LeadDetails>["suggestedContacts"][number];
/** The revealed contact returned by the on-demand reveal — the same shape from either mutation. */
type RevealedContactDetails = RevealContactMutation["revealContact"]["contact"];

/**
 * Whether the suggestion arrived carrying its details. The server sends them only for an Oscar
 * contact the company has already revealed (or on the legacy experience, where Oscar details were
 * never gated); an empty slot then arrives as `[]`, not as an absent field. Anything else is
 * revealed on demand.
 */
function detailsSupplied(suggestion: SuggestedContact): boolean {
  return (
    suggestion.email != null ||
    suggestion.mobilePhoneNumbers != null ||
    suggestion.directPhoneNumbers != null
  );
}

function suggestionSaveEntity(suggestion: SuggestedContact): EntityActionParams | null {
  // A redacted suggestion carries no `contactId` (the server withholds it from companies without
  // CONTACTS), and there is nothing to attach without one. This card is not rendered for them,
  // so treat it the same as a Cognism suggestion with no redeem handle: not saveable.
  if (!suggestion.contactId) return null;
  const entityId = suggestion.contactId;

  // `String(...)` keeps this robust whether `source` is generated as a string union or a TS enum.
  if (String(suggestion.source) === "oscar") {
    return { entityType: "contact", entityId };
  }
  // Attach by the stable `contactId`, not `redeemId`: the suggestion's redeemId is a snapshot
  // taken when the pool was generated and is never refreshed, so it may have rotated since. The
  // server resolves a current handle from the stable id; its presence here just means the
  // contact is redeemable at all.
  return suggestion.redeemId ? { entityType: "contact_cognism", entityId } : null;
}

// Reveal suggestions in pages of 5. The workflow persists a ranked pool (maxSuggestions) up front,
// so revealing the next page is instant; once the pool runs out, "load more" generates a fresh
// batch on demand (see `handleLoadMore`). Neither path ever repeats a contact.
const INITIAL_VISIBLE = 5;
const LOAD_MORE_STEP = 5;

/**
 * AI-suggested buyer contacts for a lead, rendered as collapsible cards. The header shows the
 * contact's name/role with Accept (✓) and Dismiss (✗) actions, and hovering it explains why the
 * contact was suggested; expanding a card reveals its non-PII attributes (seniority, function,
 * buyer) and loads its contact details — Cognism details are revealed on demand at that point,
 * since revealing one costs a credit and suggesting shouldn't. Accepting a suggestion attaches
 * it (reusing the standard contact save path, which redeems the contact if the user never
 * opened it) and it moves into the lead's saved contacts; both accept and dismiss refetch the lead
 * so the suggestion drops out. Suggestions are shown 5 at a time — "load more" reveals the next
 * page from the persisted pool, or generates a fresh batch once it's exhausted. Renders nothing
 * when the user lacks CONTACTS access or there are no suggestions.
 */
export function SuggestedContactsCard({
  leadId,
  suggestedContacts,
}: {
  leadId: string;
  suggestedContacts: SuggestedContact[];
}) {
  const { addLeadEntities, isLoading: isAttaching } = useLeadEntityActions({ leadId });
  const { logEvent } = useTracking();
  // Each dismissal's event, keyed by the variables it was started with. Logged from the hook's
  // own callback: one passed to `mutate` runs only for the latest of overlapping dismissals.
  const dismissEvents = useRef(new WeakMap<object, EventData>());
  const { mutate: dismissSuggestion } = useDismissLeadSuggestedContact(leadId, {
    onSuccess: (_, variables) => {
      const eventData = dismissEvents.current.get(variables);
      if (eventData) logEvent(EventNames.recommendedContactDismissed, eventData);
    },
    onError: () => toast.error("Failed to dismiss suggested contact"),
  });
  const { mutate: generateMore, isLoading: isGeneratingMore } =
    useGenerateMoreLeadSuggestedContacts(leadId, {
      onError: () => toast.error("Failed to load more suggested contacts"),
    });

  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  // Set once a top-up returns 0 new contacts — the buyer has none left, so stop offering "more".
  const [exhausted, setExhausted] = useState(false);

  // `addLeadEntities` is fire-and-forget with a single shared `isLoading`, so clear the per-card
  // accepting marker whenever an attach settles (on success the lead refetches and the card drops).
  useEffect(() => {
    if (!isAttaching) setAcceptingId(null);
  }, [isAttaching]);

  // Resolve the raw Cognism seniority-tier key (e.g. "board_governance_elected") to its human
  // label, using the same backend-served option map the contacts table uses.
  const { data: seniorityTierOptions } = useSeniorityTierOptions();
  const seniorityTierLabels = useMemo(
    () => Object.fromEntries((seniorityTierOptions ?? []).map((o) => [o.value, o.label])),
    [seniorityTierOptions],
  );

  const subscription = useSubscription();
  const hasAccess = subscription.hasDataTypes("CONTACTS");

  if (!hasAccess || suggestedContacts.length === 0) {
    return null;
  }

  // Accepting reports as "Lead data added"; the surrounding context marks it as a recommendation.
  const handleAccept = (suggestion: SuggestedContact, position: number, revealed: boolean) => {
    const saveEntity = suggestionSaveEntity(suggestion);
    if (!saveEntity) return;
    setAcceptingId(suggestion.id);
    addLeadEntities([saveEntity], {
      ...suggestionEventData(suggestion),
      "Suggestion position": position,
      "Revealed before accept": revealed,
    });
  };

  const handleDismiss = (suggestion: SuggestedContact, position: number) => {
    setDismissingId(suggestion.id);
    const variables = { leadId, suggestionId: suggestion.id };
    dismissEvents.current.set(variables, {
      ...suggestionEventData(suggestion),
      "Lead ID": leadId,
      "Suggestion position": position,
    });
    dismissSuggestion(variables, { onSettled: () => setDismissingId(null) });
  };

  const visibleSuggestions = suggestedContacts.slice(0, visibleCount);

  // Reveal the next page from the pre-generated pool when it has enough; otherwise generate a fresh
  // batch (excluding already-seen) and reveal it. The button hides only once a generation returns 0.
  const handleLoadMore = () => {
    const target = visibleCount + LOAD_MORE_STEP;
    if (suggestedContacts.length >= target) {
      setVisibleCount(target);
      logEvent(EventNames.moreRecommendedContactsRequested, {
        "Lead ID": leadId,
        Source: "Pool",
        "Returned count": LOAD_MORE_STEP,
        Exhausted: false,
      });
      return;
    }
    generateMore(
      { leadId },
      {
        onSuccess: (data) => {
          const returned = data.generateMoreLeadSuggestedContacts;
          logEvent(EventNames.moreRecommendedContactsRequested, {
            "Lead ID": leadId,
            Source: "Generated",
            "Returned count": returned,
            Exhausted: returned === 0,
          });
          if (returned > 0) {
            setVisibleCount(target);
          } else {
            setVisibleCount(suggestedContacts.length);
            setExhausted(true);
          }
        },
      },
    );
  };

  return (
    <Flex column gap={12}>
      {visibleSuggestions.map((suggestion, index) => (
        <SuggestionCard
          key={suggestion.id}
          suggestion={suggestion}
          canAccept={suggestionSaveEntity(suggestion) !== null}
          accepting={acceptingId === suggestion.id}
          dismissing={dismissingId === suggestion.id}
          attaching={isAttaching}
          seniorityTierLabels={seniorityTierLabels}
          onAccept={(revealed) => handleAccept(suggestion, index + 1, revealed)}
          onDismiss={() => handleDismiss(suggestion, index + 1)}
        />
      ))}
      {!exhausted && (
        <Flex>
          <Button
            buttonType="tertiary"
            loading={isGeneratingMore}
            onClick={handleLoadMore}
            iconProps={{ color: "sysTextPlaceholder" }}
          >
            <Text as="span" color="sysTextPlaceholder">
              {isGeneratingMore
                ? "Searching for more contacts"
                : `Recommend ${LOAD_MORE_STEP} more contacts`}
            </Text>
          </Button>
        </Flex>
      )}
    </Flex>
  );
}

function SuggestionCard({
  suggestion,
  canAccept,
  accepting,
  dismissing,
  attaching,
  seniorityTierLabels,
  onAccept,
  onDismiss,
}: {
  suggestion: SuggestedContact;
  canAccept: boolean;
  accepting: boolean;
  dismissing: boolean;
  attaching: boolean;
  seniorityTierLabels: Record<string, string>;
  /** Called with whether this card had revealed the contact's details first. */
  onAccept: (revealed: boolean) => void;
  onDismiss: () => void;
}) {
  const { logEvent } = useTracking();
  // Details are fetched only once the user actually opens the card: a Cognism suggestion by its
  // redeemId, an Oscar one the company has not revealed by its contact id. Both are free here; a
  // suggestion that arrived with its details has nothing to fetch.
  const isCognism = String(suggestion.source) === "cognism";
  const revealable = isCognism
    ? !!suggestion.redeemId
    : !!suggestion.contactId && !detailsSupplied(suggestion);
  // The same shape the contacts table reports, so lead reveals join the one reveal funnel.
  const logReveal = (outcome: RevealOutcome) =>
    logEvent(EventNames.contactsRevealed, {
      ...revealEventData("Lead suggestion", outcome),
      ...suggestionEventData(suggestion),
    });
  const logRevealStatus = (status: RevealStatus | undefined, hasContact: boolean) => {
    if (status === RevealStatus.Suppressed) {
      logReveal({ revealedCount: 0, suppressedCount: 1, failedCount: 0 });
      return;
    }
    const failed = !hasContact || status === RevealStatus.ProviderError;
    logReveal({ revealedCount: failed ? 0 : 1, suppressedCount: 0, failedCount: failed ? 1 : 0 });
  };
  const logRevealError = () => logReveal({ revealedCount: 0, suppressedCount: 0, failedCount: 1 });

  // Logged from the hooks' own callbacks, which still run if the card unmounts mid-reveal (an
  // accept or dismiss drops it); a callback passed to `mutate` would not.
  const {
    mutate: revealContact,
    data: revealData,
    isLoading: isRevealingOne,
    isError: revealOneFailed,
  } = useRevealContact({
    onSuccess: (data) => logRevealStatus(data.revealContact.status, !!data.revealContact.contact),
    onError: logRevealError,
  });
  const {
    mutate: revealContacts,
    data: revealManyData,
    isLoading: isRevealingMany,
    isError: revealManyFailed,
  } = useRevealContacts({
    onSuccess: (data) => {
      const item = data.revealContacts.perItem[0];
      logRevealStatus(item?.status, !!item?.contact);
    },
    onError: logRevealError,
  });
  const revealedOscar = revealManyData?.revealContacts.perItem[0]?.contact;
  const revealed: RevealedContactDetails | undefined =
    revealData?.revealContact.contact ?? revealedOscar ?? undefined;
  const isRevealing = isRevealingOne || isRevealingMany;
  // An Oscar item can answer without a contact (nothing behind the id); that is a failed reveal.
  const revealFailed =
    revealOneFailed || revealManyFailed || (revealManyData !== undefined && !revealedOscar);

  // Guarding on the mutation's own state keeps it to one request per card: reopening a card that
  // already has details (or has one in flight) doesn't re-request.
  const revealOnOpen = () => {
    if (!revealable || revealData || revealManyData || isRevealing) return;
    if (isCognism) {
      revealContact({ input: { redeemId: suggestion.redeemId as string } });
    } else {
      revealContacts({
        input: {
          contacts: [{ source: CognismContactSource.Oscar, id: suggestion.contactId as string }],
        },
      });
    }
  };

  // Derived from the result rather than `isRevealing`, so the panel opens on a spinner instead of
  // flashing "no details" in the frame before the request registers.
  const loadingDetails = revealable && !revealed && !revealFailed;

  const reason = suggestion.reason?.trim();

  // Accept is blocked while this card is accepting/dismissing or any attach is in flight (the
  // shared mutation refetches the lead). `accepting` guards the window before `attaching` flips so
  // a double-click can't fire a second attach. Dismiss is blocked while this card is busy.
  const acceptDisabled = attaching || accepting || dismissing || !canAccept;
  const declineDisabled = accepting || dismissing;

  return (
    <LeadContactCard
      variant="suggested"
      ariaLabel="Suggested contact card"
      name={suggestion.fullName?.trim() || "Suggested contact"}
      jobTitle={suggestion.jobTitle?.trim()}
      seniority={suggestion.seniority ? seniorityTierLabels[suggestion.seniority] ?? "-" : "-"}
      jobFunction={suggestion.jobFunction?.length ? suggestion.jobFunction.join(", ") : "-"}
      buyer={suggestion.buyer ?? null}
      explainer={
        reason ? (
          <Flex column gap={8}>
            <ReasonTitle>Why was this contact suggested?</ReasonTitle>
            <ReasonText>{reason}</ReasonText>
          </Flex>
        ) : undefined
      }
      explainerAriaLabel={reason ? `Why this contact was suggested: ${reason}` : undefined}
      onOpen={revealOnOpen}
      actions={
        <>
          <Tooltip content="Dismiss suggestion">
            {/* Span wrapper: a disabled button suppresses pointer events, so the Tooltip listens
                on the span instead — otherwise the tooltip never fires while the button is busy. */}
            <TooltipTarget>
              <IconActionButton
                type="button"
                variant="decline"
                aria-label="Dismiss suggestion"
                disabled={declineDisabled}
                onClick={onDismiss}
              >
                <UIcon icon={dismissing ? "spinner" : "xClose"} size={16} />
              </IconActionButton>
            </TooltipTarget>
          </Tooltip>
          <Tooltip content={canAccept ? "Accept suggestion" : "This contact can't be added"}>
            <TooltipTarget>
              <IconActionButton
                type="button"
                variant="accept"
                aria-label="Accept suggestion"
                disabled={acceptDisabled}
                onClick={() => onAccept(!!revealed)}
              >
                <UIcon icon={accepting ? "spinner" : "checkOutlined"} size={16} />
              </IconActionButton>
            </TooltipTarget>
          </Tooltip>
        </>
      }
      details={
        <ContactDetails
          suggestion={suggestion}
          revealed={revealed}
          loading={loadingDetails}
          failed={revealFailed}
        />
      }
    />
  );
}
/**
 * A suggestion's channels, whichever half of the card supplied them: the on-demand reveal once
 * it has answered, else what the suggestion itself arrived with (an Oscar contact the company had
 * already revealed). A Cognism suggestion never arrives with details, so before its reveal there
 * is nothing to show. Branching rather than coalescing keeps an empty array on one side from
 * masking the other's values.
 */
function suggestionChannels(
  suggestion: SuggestedContact,
  revealed?: RevealedContactDetails,
): ContactChannelData {
  if (revealed) {
    return {
      email: revealed.email,
      mobilePhoneNumbers: revealed.mobilePhoneNumbers,
      directPhoneNumbers: revealed.directPhoneNumbers,
      // Oscar carries no LinkedIn profile, so this only ever renders for a Cognism contact.
      linkedinUrl: revealed.linkedinUrl,
    };
  }
  if (String(suggestion.source) === "cognism") return {};
  return {
    email: suggestion.email,
    mobilePhoneNumbers: suggestion.mobilePhoneNumbers,
    directPhoneNumbers: suggestion.directPhoneNumbers,
  };
}

/** The same per-contact attributes the contacts table reports, from a suggestion's fields. */
function suggestionEventData(suggestion: SuggestedContact) {
  return contactEventData({
    source: String(suggestion.source),
    jobTitle: suggestion.jobTitle,
    seniorityTier: suggestion.seniority,
    jobFunction: suggestion.jobFunction,
    buyer: suggestion.buyer,
  });
}

function ContactDetails({
  suggestion,
  revealed,
  loading,
  failed,
}: {
  suggestion: SuggestedContact;
  revealed?: RevealedContactDetails;
  loading: boolean;
  failed: boolean;
}) {
  const trackingData = suggestionEventData(suggestion);

  // Which channels a reveal will return isn't known until it lands, so the in-flight placeholder
  // is the two a contact almost always has rather than this contact's own channel set.
  if (loading) {
    return (
      <div role="status" aria-label="Loading contact details">
        <ContactChannelList
          rows={visibleChannels({ hasMobilePhoneNumbers: true, hasEmail: true }, false)}
          trackingData={trackingData}
          revealing
          valueTone="default"
        />
      </div>
    );
  }

  const rows = visibleChannels(suggestionChannels(suggestion, revealed), true);
  if (rows.length > 0) {
    return <ContactChannelList rows={rows} trackingData={trackingData} valueTone="default" />;
  }

  if (failed) {
    return (
      <ContactHint>
        Couldn&apos;t load contact details. Adding the contact to the lead will unlock them.
      </ContactHint>
    );
  }

  return <ContactHint>No contact details available for this contact</ContactHint>;
}

// Wraps a (possibly disabled) button so a Tooltip has an element that still receives pointer events.
const TooltipTarget = styled.span({ display: "inline-flex" });

const ReasonTitle = styled.span(({ theme }) => ({
  fontWeight: 500,
  color: theme.colors.sysTextDefault,
}));

const ReasonText = styled.p(({ theme }) => ({
  margin: 0,
  color: theme.colors.sysTextSecondary,
}));

const IconActionButton = styled.button<{ variant: "accept" | "decline" }>(({ theme, variant }) => ({
  all: "unset",
  boxSizing: "border-box",
  width: 32,
  height: 32,
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.colors.sysTextSecondary,
  cursor: "pointer",
  transition: "background 0.1s ease, color 0.1s ease",
  "&:hover": {
    background:
      variant === "accept" ? theme.colors.sysSuccessSubtle : theme.colors.sysDestructiveSubtle,
    color:
      variant === "accept" ? theme.colors.sysSuccessDefault : theme.colors.sysDestructiveDefault,
  },
  "&:focus-visible": { outline: `2px solid ${theme.colors.sysBorderDecorative}` },
  "&:disabled": { cursor: "not-allowed", opacity: 0.4 },
  "&:disabled:hover": { background: "transparent", color: theme.colors.sysTextSecondary },
}));
